import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SaleReturnEntry() {
  const [returns, setReturns] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({ id: '', date: '', part_id: '', customer: '', qty: '', reason: '', invoice: '', created_by: '' });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => user.role === 'admin' || entry.created_by === user.username;
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0], part_id: '', customer: '', qty: '', reason: '', invoice: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [r, i, c] = await Promise.all([
        axios.get(`${API}/transactions/sale-return`), axios.get(`${API}/masters/items`), axios.get(`${API}/masters/customers`)
      ]);
      setReturns(r.data); setItems(i.data); setCustomers(c.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/transactions/sale-return`, {
        ...formData, date: new Date(formData.date).toISOString(),
        qty: parseInt(formData.qty), created_by: user.username
      });
      toast.success('Sale return recorded');
      setShowForm(false);
      setFormData({ date: new Date().toISOString().split('T')[0], part_id: '', customer: '', qty: '', reason: '', invoice: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (r) => {
    setEditData({
      id: r.id,
      date: (typeof r.date === 'string' ? r.date : new Date(r.date).toISOString()).split('T')[0],
      part_id: r.part_id,
      customer: r.customer,
      qty: r.qty.toString(),
      reason: r.reason || '',
      invoice: r.invoice || '',
      created_by: r.created_by || user.username,
    });
    setEditDialog(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/transactions/sale-return/${editData.id}`, {
        date: new Date(editData.date).toISOString(),
        part_id: editData.part_id,
        customer: editData.customer,
        qty: parseInt(editData.qty),
        reason: editData.reason,
        invoice: editData.invoice,
        created_by: editData.created_by,
      });
      toast.success('Sale return updated');
      setEditDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale return?')) return;
    try { await axios.delete(`${API}/transactions/sale-return/${id}`); toast.success('Deleted'); fetchData(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="sale-return-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="sale-return-title">SALE RETURNS</h1>
          <p className="text-sm text-text-muted mt-2">Record goods returned by customers. Automatically reduces dispatched qty and adds to FG stock.</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium" data-testid="toggle-return-form">
          <Plus className="w-4 h-4 mr-2" />{showForm ? 'CANCEL' : 'NEW RETURN'}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RECORD SALE RETURN</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
              <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required className="mt-1.5 rounded-none" data-testid="return-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part</Label>
              <SearchableSelect testId="return-part" className="mt-1.5"
                options={items.map(i => ({ value: i.part_id, label: `${i.part_id} - ${i.part_name}`, searchStr: `${i.part_id} ${i.part_name}` }))}
                value={formData.part_id} onValueChange={(v) => setFormData({...formData, part_id: v})}
                placeholder="Select part..." searchPlaceholder="Search..." />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer</Label>
              <SearchableSelect testId="return-customer" className="mt-1.5"
                options={customers.map(c => ({ value: c.customer_id, label: `${c.customer_id} - ${c.customer_name}`, searchStr: `${c.customer_id} ${c.customer_name}` }))}
                value={formData.customer} onValueChange={(v) => setFormData({...formData, customer: v})}
                placeholder="Select customer..." searchPlaceholder="Search..." />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
              <Input type="number" value={formData.qty} onChange={(e) => setFormData({...formData, qty: e.target.value})} required className="mt-1.5 rounded-none" data-testid="return-qty" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Invoice No</Label>
              <Input value={formData.invoice} onChange={(e) => setFormData({...formData, invoice: e.target.value})} placeholder="Original invoice..." className="mt-1.5 rounded-none" data-testid="return-invoice" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Reason</Label>
              <Input value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} placeholder="Return reason..." className="mt-1.5 rounded-none" data-testid="return-reason" />
            </div>
            <div className="col-span-3 flex justify-end pt-4">
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="submit-return">RECORD RETURN</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-border shadow-sm">
        <div className="border-b border-border p-4 bg-gray-50/50">
          <h2 className="text-xl font-semibold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RETURN LOG</h2>
        </div>
        {returns.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No sale returns recorded</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b">
                <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Customer</th>
                <th className="h-10 px-4 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Qty</th>
                <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Invoice</th>
                <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Reason</th>
                <th className="h-10 px-4 text-center font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
              </tr></thead>
              <tbody>
                {returns.map((r, idx) => (
                  <tr key={r.id} className="border-b hover:bg-muted/50" data-testid={`return-row-${idx}`}>
                    <td className="p-4 font-mono text-xs">{new Date(r.date).toLocaleDateString()}</td>
                    <td className="p-4 font-mono text-xs">{r.part_id}</td>
                    <td className="p-4 text-xs">{r.customer}</td>
                    <td className="p-4 text-right font-mono text-xs font-semibold text-emerald-600">+{r.qty}</td>
                    <td className="p-4 text-xs">{r.invoice || '-'}</td>
                    <td className="p-4 text-xs">{r.reason || '-'}</td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {canModify(r) && <button onClick={() => openEdit(r)} className="p-2 hover:bg-blue-50 hover:text-blue-600 rounded-sm" title="Edit Return" data-testid={`edit-return-${idx}`}><Pencil className="w-4 h-4" /></button>}
                        {canModify(r) && <button onClick={() => handleDelete(r.id)} className="p-2 hover:bg-error/10 hover:text-error rounded-sm" title="Delete Return" data-testid={`delete-return-${idx}`}><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>EDIT SALE RETURN</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
              <Input type="date" value={editData.date} onChange={(e) => setEditData({...editData, date: e.target.value})} required className="mt-1.5 rounded-none" data-testid="edit-return-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part</Label>
              <SearchableSelect testId="edit-return-part" className="mt-1.5"
                options={items.map(i => ({ value: i.part_id, label: `${i.part_id} - ${i.part_name}`, searchStr: `${i.part_id} ${i.part_name}` }))}
                value={editData.part_id} onValueChange={(v) => setEditData({...editData, part_id: v})}
                placeholder="Select part..." searchPlaceholder="Search..." />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer</Label>
              <SearchableSelect testId="edit-return-customer" className="mt-1.5"
                options={customers.map(c => ({ value: c.customer_id, label: `${c.customer_id} - ${c.customer_name}`, searchStr: `${c.customer_id} ${c.customer_name}` }))}
                value={editData.customer} onValueChange={(v) => setEditData({...editData, customer: v})}
                placeholder="Select customer..." searchPlaceholder="Search..." />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
              <Input type="number" value={editData.qty} onChange={(e) => setEditData({...editData, qty: e.target.value})} required className="mt-1.5 rounded-none" data-testid="edit-return-qty" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Invoice No</Label>
              <Input value={editData.invoice} onChange={(e) => setEditData({...editData, invoice: e.target.value})} placeholder="Original invoice..." className="mt-1.5 rounded-none" data-testid="edit-return-invoice" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Reason</Label>
              <Input value={editData.reason} onChange={(e) => setEditData({...editData, reason: e.target.value})} placeholder="Return reason..." className="mt-1.5 rounded-none" data-testid="edit-return-reason" />
            </div>
            <div className="col-span-3 flex justify-end gap-3 pt-4">
              <Button type="button" onClick={() => setEditDialog(false)} className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-sm">CANCEL</Button>
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="save-return-edit">SAVE CHANGES</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
